import {
  Context,
  createContext,
  FC,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  ICategoryAsset,
  ICategoryLiteAsset,
  ICategoryLiteWithChildsAsset,
  IVirtualCatAsset,
  IVirtualCatLiteAsset,
} from "models/Assets/ICategoryAsset";
import { ICityAsset, ICityLiteAsset } from "models/Assets/ICityAsset";
import { ICountryAsset, ICountryAssetLite } from "models/Assets/ICountryAsset";
import { INeighborhoodLiteAsset } from "models/Assets/INeighborhoodAsset";
import {
  IDynamicAttributeEnhanced,
  IDynamicAttributeSortField,
} from "models/Assets/IDynamicAttributes";
import { getDefaultOrderModes } from "helpers/osHelpers";
import { filterCityByCityId } from "dataLayer/filters/citiesAndHoods";
import { __getAjaxClient } from "utils/misc/fetchClient";
import { IRouteObjectsIds } from "contexts/RoutesObjectContext";
import { qsStringify } from "helpers/globalHelpers";
import { IListingAssets } from "contexts/ListingContextHelpers/interfaces";

export const ALL_CATEGORIES_ITEM: ICategoryLiteAsset = {
  icon: "",
  id: 0,
  label_ar: "جميع الاقسام",
  label_en: "All Categories",
  reporting_name: "All Categories",
  subCategories: [],
  url_name_ar: "All Categories",
  url_name_en: "All Categories",
};

export const ALL_CITIES_ITEM: ICityLiteAsset = {
  id: 0,
  name_arabic: "جميع المدن",
  name_english: "All Cities",
  url_name_ar: "جميع المدن",
  url_name_en: "All Cities",
};

export type AllowedCitiesTypes = ICityLiteAsset | ICityAsset;
export type AllowedCategoriesTypes =
  | ICategoryLiteAsset
  | ICategoryAsset
  | ICategoryLiteWithChildsAsset;
export type AllowedVirtualCategoriesTypes =
  | IVirtualCatAsset
  | IVirtualCatLiteAsset;

export type AllowedCountriesTypes = ICountryAsset | ICountryAssetLite;

export interface IAssetsContext {
  isLoaded?: boolean;
  categories?: AllowedCategoriesTypes[];
  subCategories?: AllowedCategoriesTypes[];
  cities?: AllowedCitiesTypes[];
  countries?: AllowedCountriesTypes[];
  neighborhoodsAssets?: INeighborhoodLiteAsset[];
  virtualCategories?: AllowedVirtualCategoriesTypes[];
  dynamicAttributesEnhanced?: IDynamicAttributeEnhanced[];
  loadAssetsByCat_SubCat_City?: (routeObjectIds: IRouteObjectsIds) => void;
}

// Create Context

export const AssetsContext: Context<IAssetsContext> = createContext({
  categories: undefined,
  subCategories: undefined,
  cities: undefined,
  countries: undefined,
  neighborhoodsAssets: undefined,
  virtualCategories: undefined,
  dynamicAttributesEnhanced: undefined,
  loadAssetsByCat_SubCat_City: undefined,
});

//Main Categories

export function useGetMainCategoriesAssets<T extends AllowedCategoriesTypes>(
  withAll: boolean = false
): T[] {
  if (withAll) {
    return [
      ALL_CATEGORIES_ITEM,
      ...useContext(AssetsContext).categories,
    ] as T[];
  }
  return useContext(AssetsContext).categories as T[];
}

//Main Categories

export function useGetVirtualCategoriesAssets<
  T extends AllowedVirtualCategoriesTypes
>(): T[] {
  return useContext(AssetsContext).virtualCategories as T[];
}

//Sub Categories

export function useGetSubCategoriesAssets<
  T extends AllowedCategoriesTypes
>(): T[] {
  return useContext(AssetsContext).subCategories as T[];
}

//Countries

export function useGetCountriesAssets<T extends AllowedCountriesTypes>(): T[] {
  return useContext(AssetsContext).countries as T[];
}

//Cities

export function useGetCitiesAssets<T extends AllowedCitiesTypes>(
  withAll: boolean = false
): T[] {
  if (withAll) {
    return [ALL_CITIES_ITEM, ...useContext(AssetsContext).cities] as T[];
  }
  return [...(useContext(AssetsContext).cities || [])] as T[];
}

export function useGetCityAsset(cityId: number | string): ICityLiteAsset {
  const { cities } = useContext(AssetsContext);
  return filterCityByCityId(cities, cityId);
}

// filtered cities
export function useGetFilteredCitiesAssets<
  T extends AllowedCitiesTypes
>(): T[] {
  return useContext(AssetsContext).cities.filter((city) => {
    if (
      ["aroundMe", "all cities", "Couldn`t listing your city?"].indexOf(
        city?.name_english
      ) < 0
    ) {
      return city;
    }
  }) as T[];
}

//Neighborhoods

export function useGetNeighborhoodsAssets(): INeighborhoodLiteAsset[] {
  return useContext(AssetsContext).neighborhoodsAssets;
}

//dynamicAttributeEnhanced
function _getDynamicAttributeEnhancedAssets(dynamicAttributesEnhanced): {
  dynamicAttributeEnhancedAssets: IDynamicAttributeEnhanced[];
  allSortCasesForSelectedCategory: IDynamicAttributeSortField[];
} {
  let sortingFields = [];
  (dynamicAttributesEnhanced || []).forEach((attr) => {
    sortingFields = [...sortingFields, ...(attr.field.sortArr || [])];
  });
  const defaultOrderModes = getDefaultOrderModes();
  const allSortCasesForSelectedCategory = [
    ...defaultOrderModes,
    ...sortingFields,
  ];
  return {
    dynamicAttributeEnhancedAssets: dynamicAttributesEnhanced,
    allSortCasesForSelectedCategory,
  };
}

export function useGetDynamicAttributeEnhancedAssets(): {
  dynamicAttributeEnhancedAssets: IDynamicAttributeEnhanced[];
  allSortCasesForSelectedCategory: IDynamicAttributeSortField[];
} {
  const { dynamicAttributesEnhanced } = useContext(AssetsContext);
  return _getDynamicAttributeEnhancedAssets(dynamicAttributesEnhanced);
}

//get Whole Asset
export function useGetWholeAssets(): { isLoaded: boolean } & IListingAssets {
  const {
    isLoaded,
    categories,
    subCategories,
    cities,
    neighborhoodsAssets,
    dynamicAttributesEnhanced,
  } = useContext(AssetsContext);
  const { dynamicAttributeEnhancedAssets, allSortCasesForSelectedCategory } =
    _getDynamicAttributeEnhancedAssets(dynamicAttributesEnhanced);
  return {
    isLoaded,
    categories,
    subCategories,
    cities,
    nHoods: neighborhoodsAssets,
    dynamicAttributeEnhancedAssets,
    allSortCasesForSelectedCategory,
  };
}

//fetch assets with api
export function useLoadAssetsByCat_SubCat_City(): (
  routeObjectIds: IRouteObjectsIds
) => void {
  return useContext(AssetsContext).loadAssetsByCat_SubCat_City;
}

//isLoaded
export function useAreAssetsLoaded(): boolean {
  return useContext(AssetsContext).isLoaded;
}

const onAssetsFailedCb = (e) => {
  window.dispatchEvent(
    new CustomEvent("assetsFailedToLoad", { detail: { message: e.message } })
  );
};

// Assets loading pub/sub hook
export function useDoWhenAssetsLoaded(callback: () => void) {
  const cb = () => {
    callback();
    window.removeEventListener("assetsLoaded", cb);
  };
  useEffect(() => {
    window.addEventListener("assetsLoaded", cb);
    return () => {
      window.removeEventListener("assetsLoaded", cb);
    };
  }, []);
}

const AssetsContextProvider: FC<IAssetsContext> = ({
  children,
  ...assetsData
}) => {
  const [bucket, setBucket] = useState<IAssetsContext>(assetsData);
  const [isLoaded, setIsLoaded] = useState<boolean>(
    assetsData.cities?.length > 0
  );
  useEffect(() => {
    if (!isLoaded && bucket.cities?.length > 0) {
      setIsLoaded(true);
    }
  }, [bucket.cities?.length]);

  useEffect(() => {
    if (isLoaded) {
      //console.log("Event Triggered: assetsLoaded");
      window.dispatchEvent(new Event("assetsLoaded"));
      window["isAssetsLoaded"] = true;
    }
  }, [isLoaded]);

  function loadAssetsByCat_SubCat_City(routeObjectIds: IRouteObjectsIds) {
    if (isLoaded) return;
    const paramsStr = qsStringify(routeObjectIds);
    __getAjaxClient<IAssetsContext>(`/loadAssetsByCat_SubCat_City${paramsStr}`)
      .then((response) => {
        setBucket({ ...bucket, ...response });
      })
      .catch(onAssetsFailedCb);
  }

  return (
    <AssetsContext.Provider
      value={{ ...bucket, isLoaded, loadAssetsByCat_SubCat_City }}
    >
      {children}
    </AssetsContext.Provider>
  );
};

export default AssetsContextProvider;
